import { useEffect, useState } from "react";
import { Api, ApiError, type ActivityItem, type FieldChange, type VersionItem } from "../api.js";
import { hasKey, useI18n, type Key, type Translate } from "../i18n.js";
import { Badge, Field } from "./ui.js";

const ACTIONS = ["created", "edited", "published", "unpublished", "reverted", "deleted", "source_added", "source_removed", "transferred", "shared", "unshared"] as const;

/** The setting's name in plain words, from the translation table; unknown settings fall back to "other settings". */
export function fieldLabel(path: string, t: Translate): string {
  // the setting itself, then its parents (leadCapture.crm.webhookUrl -> leadCapture.crm)
  const parts = path.split(".");
  for (let i = parts.length; i > 0; i--) {
    const key = `act.f.${parts.slice(0, i).join(".")}`;
    if (hasKey(key)) return t(key);
  }
  return t("act.f.other");
}

function who(a: ActivityItem["actor"], t: Translate): string {
  if (a.kind === "system") return t("act.who.system");
  if (a.kind === "partner") return t("act.who.partner");
  if (a.kind === "api_key") return t("act.who.key", { name: a.label });
  return a.label;
}

const sourceName = (s: ActivityItem["summary"], t: Translate): string => {
  const kind = s.type === "text" || s.type === "file" || s.type === "url" || s.type === "crawl" ? (`act.src.${s.type}` as Key) : ("act.src.other" as Key);
  return `${t(kind)}${s.host ? ` (${s.host})` : s.extension ? ` (.${s.extension})` : ""}`;
};

/** One line of history in plain words. */
export function describe(item: ActivityItem, t: Translate): string {
  const w = who(item.actor, t);
  switch (item.action) {
    case "edited": {
      const labels = [...new Set((item.summary.fields ?? []).map((f) => fieldLabel(f.path, t)))];
      const shown = labels.slice(0, 3).join(", ");
      return t("act.action.edited", { who: w, what: labels.length > 3 ? t("act.andMore", { list: shown, n: labels.length - 3 }) : shown || t("act.f.other") });
    }
    case "published":
      return t(item.summary.first ? "act.action.publishedFirst" : "act.action.published", { who: w, n: item.version ?? "" });
    case "reverted":
      return t("act.action.reverted", { who: w, n: item.version ?? "" });
    case "source_added":
    case "source_removed":
      return t(item.action === "source_added" ? "act.action.source_added" : "act.action.source_removed", { who: w, what: sourceName(item.summary, t) });
    default:
      return t(`act.action.${item.action}` as Key, { who: w });
  }
}

function change(f: FieldChange, t: Translate): string {
  const val = (v: unknown) => (Array.isArray(v) ? v.join(", ") : String(v));
  if (f.old !== undefined || f.new !== undefined) {
    if (f.old !== undefined && f.new !== undefined) return t("act.fromTo", { from: val(f.old), to: val(f.new) });
    return f.new !== undefined ? val(f.new) : t("act.changedCleared");
  }
  const from = f.size?.from;
  const to = f.size?.to;
  const unit = f.size?.unit === "items" ? "act.items" : "act.chars";
  if (f.change === "cleared") return t("act.changedCleared");
  if (to !== undefined) return `${t("act.changedOnly")} (${t(unit as Key, { n: to })}${from !== undefined && f.change === "changed" ? `, ${t("act.fromTo", { from: String(from), to: String(to) })}` : ""})`;
  return f.change === "set" ? t("act.changedSet") : t("act.changedOnly");
}

export function ActivityPage({ api, agentId, tab, agentName }: { api: Api; agentId: string; tab: "activity" | "versions"; agentName?: string }) {
  const { t, lang } = useI18n();
  const [name, setName] = useState(agentName ?? "");
  useEffect(() => {
    if (agentName) return;
    api.getAgent(agentId).then((a) => setName(String((a.config as { name?: string }).name ?? "")), () => undefined);
  }, [api, agentId, agentName]);

  return (
    <main className="page" id="main">
      <p>
        <a href={`#/agents/${agentId}`}>← {t("act.back")}</a>
      </p>
      <h1>{t("act.title", { name: name || "…" })}</h1>
      <p>{t("act.lead")}</p>
      <nav className="tabs" aria-label={t("act.title", { name: name || "" })}>
        <a href={`#/agents/${agentId}/activity`} aria-current={tab === "activity" ? "page" : undefined}>
          {t("act.tab.activity")}
        </a>
        <a href={`#/agents/${agentId}/versions`} aria-current={tab === "versions" ? "page" : undefined}>
          {t("act.tab.versions")}
        </a>
      </nav>
      {tab === "activity" ? <ActivityList api={api} agentId={agentId} lang={lang} /> : <Versions api={api} agentId={agentId} lang={lang} />}
    </main>
  );
}

function ActivityList({ api, agentId, lang }: { api: Api; agentId: string; lang: string }) {
  const { t } = useI18n();
  const [action, setAction] = useState("");
  const [actor, setActor] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [items, setItems] = useState<ActivityItem[] | null>(null);
  const [people, setPeople] = useState<Map<string, string>>(new Map());
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<{ forbidden: boolean; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async (offset: number) => {
    setBusy(true);
    try {
      const r = await api.activity(agentId, { action, actor, from, to, offset, limit: 25 });
      setItems((cur) => (offset === 0 ? r.activity : [...(cur ?? []), ...r.activity]));
      setTotal(r.total);
      setPeople((cur) => {
        const m = new Map(cur);
        for (const a of r.activity) if (a.actor.kind === "user" && a.actor.id) m.set(a.actor.id, a.actor.label);
        return m;
      });
      setError(null);
    } catch (e) {
      setError({ forbidden: e instanceof ApiError && e.status === 403, message: e instanceof ApiError ? e.message : t("common.network") });
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    void load(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, agentId, action, actor, from, to]);

  if (error) return <div className="banner warn" role="alert">{error.forbidden ? t("act.forbidden") : error.message}</div>;
  const filtered = !!(action || actor || from || to);
  return (
    <>
      <div className="card filters">
        <Field label={t("act.filter.action")} htmlFor="act-action">
          <select id="act-action" value={action} onChange={(e) => setAction(e.target.value)}>
            <option value="">{t("act.filter.any")}</option>
            {ACTIONS.map((a) => (
              <option key={a} value={a}>
                {t(`act.a.${a}` as Key)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("act.filter.person")} htmlFor="act-actor">
          <select id="act-actor" value={actor} onChange={(e) => setActor(e.target.value)}>
            <option value="">{t("act.filter.any")}</option>
            {[...people.entries()].map(([id, label]) => (
              <option key={id} value={id}>
                {label}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t("act.filter.from")} htmlFor="act-from">
          <input id="act-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
        </Field>
        <Field label={t("act.filter.to")} htmlFor="act-to">
          <input id="act-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
        </Field>
      </div>
      {items === null ? (
        <p>{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <p className="hint">{filtered ? t("act.noMatch") : t("act.empty")}</p>
      ) : (
        <ol className="timeline">
          {items.map((i) => (
            <li key={i.id}>
              <p className="line">
                <time dateTime={i.at}>{new Date(i.at).toLocaleString(lang)}</time> · {describe(i, t)}
                {i.client && <span className="hint"> ({t("act.via", { client: i.client })})</span>}
              </p>
              {!!i.summary.fields?.length && (
                <details>
                  <summary>{i.summary.fields.length}</summary>
                  <ul>
                    {i.summary.fields.map((f) => (
                      <li key={f.path}>
                        <strong>{fieldLabel(f.path, t)}:</strong> {change(f, t)}
                      </li>
                    ))}
                  </ul>
                </details>
              )}
            </li>
          ))}
        </ol>
      )}
      {items && items.length < total && (
        <button className="btn secondary" onClick={() => void load(items.length)} disabled={busy}>
          {t("act.more")}
        </button>
      )}
    </>
  );
}

function Versions({ api, agentId, lang }: { api: Api; agentId: string; lang: string }) {
  const { t } = useI18n();
  const [versions, setVersions] = useState<VersionItem[] | null>(null);
  const [error, setError] = useState<{ forbidden: boolean; message: string } | null>(null);
  const [asking, setAsking] = useState<number | null>(null);
  const [done, setDone] = useState<number | null>(null);
  const [failed, setFailed] = useState<{ message: string; upgradeUrl?: string } | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.versions(agentId).then(setVersions, (e) => setError({ forbidden: e instanceof ApiError && e.status === 403, message: e instanceof ApiError ? e.message : t("common.network") }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, agentId]);

  const revert = async (n: number) => {
    setBusy(true);
    setFailed(null);
    try {
      await api.revertTo(agentId, n);
      setDone(n);
      setAsking(null);
    } catch (e) {
      setFailed({ message: t("act.v.failed", { why: e instanceof ApiError ? (e.issues[0]?.message ?? e.message) : t("common.network") }), upgradeUrl: e instanceof ApiError && e.status === 402 && typeof e.body.upgradeUrl === "string" ? e.body.upgradeUrl : undefined });
    } finally {
      setBusy(false);
    }
  };

  if (error) return <div className="banner warn" role="alert">{error.forbidden ? t("act.forbidden") : error.message}</div>;
  if (!versions) return <p>{t("common.loading")}</p>;
  return (
    <>
      <p>{t("act.v.lead")}</p>
      {done !== null && (
        <div className="banner ok" role="status">
          <p>{t("act.v.done", { n: done })}</p>
          <a className="btn primary" href={`#/agents/${agentId}`}>
            {t("act.v.review")}
          </a>
        </div>
      )}
      {failed && (
        <div className="banner warn" role="alert">
          <p>{failed.message}</p>
          {failed.upgradeUrl && (
            <a className="btn primary" href={failed.upgradeUrl}>
              {t("upgrade.cta")}
            </a>
          )}
        </div>
      )}
      {versions.length === 0 ? (
        <p className="hint">{t("act.v.empty")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>{t("act.v.version")}</th>
                <th>{t("act.v.when")}</th>
                <th>{t("act.v.by")}</th>
                <th>
                  <span className="sr-only">{t("act.v.revert")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.version}>
                  <td>
                    {v.version} {v.current && <Badge tone="ok">{t("act.v.live")}</Badge>}
                    {v.name && <div className="hint">{v.name}</div>}
                  </td>
                  <td>
                    <time dateTime={v.publishedAt}>{new Date(v.publishedAt).toLocaleString(lang)}</time>
                  </td>
                  <td>{v.by ?? "—"}</td>
                  <td>
                    {asking === v.version ? (
                      <div role="alertdialog" aria-label={t("act.v.revertAria", { n: v.version })}>
                        <p>{t("act.v.confirm", { n: v.version })}</p>
                        <div className="row">
                          <button className="btn primary" onClick={() => void revert(v.version)} disabled={busy}>
                            {t("act.v.confirmYes")}
                          </button>
                          <button className="btn ghost" onClick={() => setAsking(null)} disabled={busy}>
                            {t("common.cancel")}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button className="btn secondary" aria-label={t("act.v.revertAria", { n: v.version })} onClick={() => { setAsking(v.version); setDone(null); setFailed(null); }}>
                        {t("act.v.revert")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
